import React, { useState, useMemo, useEffect } from "react";
import IconList from "./components/IconList";
import { Nav, Heading, TextField } from "@caesars-ui/core";
import { icons as uiIcons } from "tokens/tokens/icons/ui-icons/ui-icons.json";
import { icons as sportsIcons } from "tokens/tokens/icons/sports-icons/sports-icons.json";
import { icons as countryFlags } from "tokens/tokens/icons/country-flags/country-flags.json";
import { icons as brandIcons } from "tokens/tokens/icons/brand-icons/brand-icons.json";
import { icons as sportsFlagIcons } from "tokens/tokens/icons/sports-flag-icons/sports-flag-icons.json";
import teams from "tokens/s3assets/json/teams.json";
import fuzzysort from "fuzzysort";

const filterTeams = ["National Basketball Association", "National Football League", "Major League Baseball", "National Hockey League"];

const allIcons = [...uiIcons, ...sportsIcons, ...countryFlags, ...brandIcons, ...sportsFlagIcons];
const allTeams = Object.entries(teams.teams)
  .map(([league, teams]) => Object.values(teams))
  .flat()
  .filter(t => {
    return !filterTeams.includes(t.name)
  });


const tabMenu = [
  { id: 0, label: "UI", search: "icon_ui" },
  { id: 1, label: "Brand", search: "brand" },
  { id: 2, label: "Flags", search: "flag" },
  { id: 3, label: "Team Logos", search: "logo" },
  { id: 4, label: "Sports", search: "icon_sports" },
  { id: 5, label: "Sports Flag", search: "sports_flag" },
];

const tabMenuLogos = [
  { id: 0, label: "MLB", search: "mlb" },
  { id: 1, label: "NBA", search: "nba" },
  { id: 2, label: "NCAA Baseball", search: "college", sport: "baseball" },
  { id: 3, label: "NCAAB", search: "college", sport: "basketball" },
  { id: 4, label: "NCAAF", search: "college", sport: "americanfootball" },
  { id: 5, label: "NCAAH", search: "college", sport: "hockey" },
  { id: 6, label: "NFL", search: "nfl" },
  { id: 7, label: "NHL", search: "nhl" },
  { id: 8, label: "Rugby", search: "rugby" },
  { id: 9, label: "Soccer", search: "soccer", sport: "soccer" },
  { id: 10, label: "WNBA", search: "wnba", sport: "basketball" },
];

const tabMenuAltLogos = [
  { id: 0, label: "Default", type: undefined },
  { id: 1, label: "Helmet", type: "helmet" }
]

const IconListContainer = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTabId, setActiveTabId] = useState(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      const tabIndex = tabMenu.findIndex(tab => tab.label.toLowerCase().replace(' ', '-') === hash);
      return tabIndex >= 0 ? tabIndex : 0;
    }
    return 0;
  });
  const [activeLogosTabId, setActiveLogosTabId] = useState(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      const tabIndex = tabMenuLogos.findIndex(tab => tab.label.toLowerCase().replace(' ', '-') === hash);
      return tabIndex >= 0 ? tabIndex : 0;
    }
    return 0;
  });
  const [activeAltLogosTabId, setActiveAltLogosTabId] = useState(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash === 'helmet') return 1;
    }
    return 0;
  });

  const logoActive = activeTabId === 3;

  const activeLogoLabel = useMemo(()=>{
    return tabMenuLogos.filter(tab => tab.id === activeLogosTabId)[0].label?.toLowerCase()
  },[activeLogosTabId])

  // Update URL hash when tab changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      let hash;
      if (logoActive && activeLogoLabel === 'nfl' && activeAltLogosTabId === 1) {
        hash = 'helmet';
      } else {
        const activeTab = logoActive ? tabMenuLogos[activeLogosTabId] : tabMenu[activeTabId];
        hash = activeTab.label.toLowerCase().replace(' ', '-');
      }
      window.history.replaceState(null, null, `#${hash}`);
    }
  }, [activeTabId, activeLogosTabId, activeAltLogosTabId, logoActive, activeLogoLabel]);

  const hiddenSearch = useMemo(() => {
    return tabMenu.filter((tab) => tab.id === activeTabId)[0].search || "";
  }, [activeTabId]);

  const activeTeams = useMemo(() => {
    const activeLeague = tabMenuLogos.filter(
      (t) => t.id === activeLogosTabId
    )[0].search;
    return Object.values(teams.teams[activeLeague]).filter(t => !filterTeams.includes(t.name));
  }, [activeLogosTabId]);

  const activeSport = useMemo(() => {
    return tabMenuLogos.filter(
        (t) => t.id === activeLogosTabId
    )[0].sport;
  }, [activeLogosTabId]);

  const activeAltLogo = useMemo(()=>{
    return tabMenuLogos.filter(
        (t) => t.id === activeLogosTabId
    )[0];
  },[activeAltLogosTabId])

  const hasSearch = useMemo(() => !!searchTerm, [searchTerm]);
 
  const filteredIcons = useMemo(() => {
    const opts = {
      threshold: -200,
      keys: ["tags", "name"],
    };
    if (!logoActive) {
      return fuzzysort.go(
        hasSearch ? searchTerm : hiddenSearch,
        allIcons,
        opts
      );
    } else {
      if (hasSearch) return fuzzysort.go(
        searchTerm,
        allTeams,
        {...opts, keys: ["name"]}
      );
      return fuzzysort.go(
        searchTerm,
        activeTeams,
        {...opts, all: true}
      );
    }
  }, [searchTerm, activeTabId, activeLogosTabId, activeSport]);

  return (
    <>
      <div>
        <Nav
          items={tabMenu}
          initialActive={0}
          className="mb-xl"
          onActiveItem={(id) => {
            setActiveTabId(id);
            setActiveLogosTabId(0); // Reset sub-tab when main tab changes
          }}
          variant="pill"
          type="default"
          size="md"
          simple
        />
        {logoActive ? (
          <Nav
            items={tabMenuLogos}
            initialActive={activeLogosTabId}
            className="mb-xl"
            onActiveItem={(id) => {
              setActiveLogosTabId(id);
            }}
            variant="pill"
            type="minimal"
            size="md"
            simple
          />
        ) : null}
        {logoActive && activeLogoLabel === "nfl" ? (
          <Nav
            items={tabMenuAltLogos}
            initialActive={0}
            className="mb-xl ml-xl"
            onActiveItem={(id) => {
              setActiveAltLogosTabId(id);
            }}
            variant="pill"
            type="minimal"
            size="md"
            simple
          />
        ) : null}
      </div>

      <div className="mb-xl">
        <TextField
          label="Filter icons by name or keyword"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-[300px]"
        />
      </div>
      <IconList
        icons={filteredIcons}
        hasSearchTerm={hasSearch}
        useTeamLogo={logoActive}
        activeSport={activeSport || ''}
        altLogoType={tabMenuAltLogos[activeAltLogosTabId]}
      />
    </>
  );
};

export default IconListContainer;
